import { useMemo, type FC, type ReactNode } from 'react';
import type { TablePaginationConfig, ColumnsType } from 'antd/es/table';
import { Link } from 'react-router';
import { Table, Spin, Empty } from 'antd';
import { Text, Button, Space } from '@g2u/ui-kit';
import { format, isValid, parse, parseISO } from 'date-fns';
import { PlusOutlined } from '@ant-design/icons';
import cx from 'classnames';

import { type Ticket } from 'types';

import styles from './ticket-list.module.scss';

type Action =
  | ((record: Ticket) => ReactNode)
  | {
      title: string;
      action: (record: Ticket) => void;
    };

type PaginationMetadata = {
  total: number;
  current_page: number;
  pageSize?: number;
  onChange: TablePaginationConfig['onChange'];
};

type Props = {
  tickets?: Ticket[];
  actions?: Action[];
  paginationMetadata?: PaginationMetadata;
  hideColumns?: ColumnKeys[];
  isLoading?: boolean;
  onAdd?: () => void;
  footer?: ReactNode;
};

type ColumnKeys = (typeof columnsDefinition)[number]['key'];

const columnsDefinition = [
  {
    key: 'ticketId',
    title: 'Ticket ID',
    dataIndex: 'ID',
    render: (_, record) => (
      <Link
        to={`/tasks/tickets?search_type=record_id&search_value=${record.ID}&status=${record.open}&expanded_id=${record.ID}`}
        title="Edit"
      >
        {record.ID}
      </Link>
    ),
    width: 70,
  },
  {
    key: 'created',
    title: 'Created',
    dataIndex: 'created_at',
    width: 60,
    render: (_, record) => (
      <>
        {record.created_at && isValid(parseISO(record.created_at))
          ? format(parse(record.created_at, 'yyyy-MM-dd HH:mm:ss', new Date()), 'dd-MM-yyyy hh:mmaaa').toLowerCase()
          : 'N/A'}
      </>
    ),
  },
  {
    key: 'customer',
    title: 'Customer',
    render: (_, record) => <>{record.task_links?.customer?.name?.full_name || 'N/A'}</>,
    width: 50,
  },
  {
    key: 'type',
    title: 'Type',
    dataIndex: 'type',
    width: 80,
  },
  {
    key: 'jobId',
    title: 'Job ID',
    dataIndex: 'job_ID',
    render: (_, record) => (
      <Link to={`/job/edit/${record.job_ID}/summary/`} title="Edit">
        {record.job_ID || ''}
      </Link>
    ),
    width: 50,
  },
  {
    key: 'technician',
    title: 'Technician',
    render: (_, record) => <>{record.technician?.name || 'N/A'}</>,
    width: 50,
  },
  {
    key: 'description',
    title: 'Description',
    dataIndex: 'description',
    width: 300,
  },
  {
    key: 'status',
    title: 'Status',
    dataIndex: 'status',
    width: 50,
  },
] as const satisfies ColumnsType<Ticket>;

const defaultPageSize = 5;

export const TicketList: FC<Props> = ({
  tickets,
  paginationMetadata: meta,
  isLoading,
  hideColumns = [],
  onAdd,
  footer,
}) => {
  const columns = useMemo(() => {
    return (columnsDefinition as ColumnsType<Ticket>).filter(
      ({ key }) => !(key && (hideColumns as string[]).includes(key as string)),
    );
  }, [columnsDefinition, hideColumns]);

  if (!tickets) {
    return <Spin data-testid="spinner" className={styles.ticketList__spin} />;
  }

  const AddNewButton: FC = () => {
    return onAdd ? (
      <Button.$ type={Button.Type.Primary} onClick={onAdd} dataTestId="create-new-ticket" icon={<PlusOutlined />}>
        New Ticket
      </Button.$>
    ) : (
      <> </>
    );
  };

  if (tickets && !tickets.length) {
    return (
      <Space.$ direction={Space.Direction.Vertical}>
        <br />
        <Empty
          styles={{ image: { height: 60 } }}
          style={{ marginBottom: 20 }}
          description={<Text.$ type="secondary">Sorry, there are no matching results.</Text.$>}
        />
        <div>
          <AddNewButton />
        </div>
      </Space.$>
    );
  }

  const paginationProps: TablePaginationConfig | undefined =
    meta && meta.total > (meta.pageSize || defaultPageSize)
      ? {
          style: { paddingTop: 12 },
          rootClassName: styles.ticketList__pagination,
          total: meta.total,
          current: meta.current_page,
          showTotal: () => (
            <Space.$ justify={Space.Justify.SpaceBetween}>
              <AddNewButton />
              <span>Total {meta.total} items</span>
            </Space.$>
          ),
          showSizeChanger: true,
          pageSize: meta.pageSize || defaultPageSize,
          pageSizeOptions: [defaultPageSize, 10, 20, 50, 100],
          onChange: typeof meta.onChange === 'function' ? meta.onChange : undefined,
          showLessItems: true,
          hideOnSinglePage: false,
          className: styles.ticketList__footer,
        }
      : {
          showTotal: () => (
            <Space.$ justify={Space.Justify.Start}>
              <AddNewButton />
            </Space.$>
          ),
          className: styles.ticketList__footer,
        };

  return (
    <Table
      pagination={paginationProps}
      rowKey={'ID'}
      loading={isLoading}
      columns={columns}
      dataSource={tickets}
      footer={footer ? () => footer : undefined}
      className={cx(styles.ticketList, !!footer && styles.ticketList_hasFooter)}
    />
  );
};
